import React, {useState, useRef} from "react";
import {useMutation} from "@tanstack/react-query";
import createUser from "../api/createUser";
import {isValidPassword} from "../helpers/passwordValidation";
import {emailFormatValidation} from "../helpers/emailFormatValidation";
import InputCodeForm from "./InputCodeForm";
import Input from "./Input";
import {ReactComponent as UploadIcon} from "../images/upload-icon.svg";
import {ReactComponent as RemoveIcon} from "../images/close-icon.svg";
import Spinner from "./Spinner";

type RegisterCardProps = {
  email: string;
};

const RegisterCard = ({email}: RegisterCardProps) => {
  const initialState = {
    profileImage: "",
    firstName: "",
    lastName: "",
    companyName: "",
    email: email,
    password: "",
    confirmPassword: "",
  };

  const [formData, setFormData] = useState(initialState);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [code, setCode] = useState(["", "", "", ""]);
  const [validateBlk, setValidateBlk] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [verifyCodeModal, setShowVerifyCodeModal] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onInputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    setMessage("");
    setValidateBlk(false);
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onClickUploadImage = () => {
    fileInputRef.current?.click();
  };

  const onFileChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          profileImage: reader.result as string,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const onRemoveImageHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFormData((prev) => ({...prev, profileImage: ""}));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const createUserMutation = useMutation({
    mutationFn: createUser,
    onSuccess: async () => {
      setFormData(initialState);
    },
    onError: (error) => {
      setValidateBlk(true);
      setMessage(error.message);
    },
  });

  const onSubmitHandler = async () => {
    const {firstName, lastName, companyName, email, password, confirmPassword} =
      formData;

    if (
      !firstName ||
      !lastName ||
      !companyName ||
      !email ||
      !password ||
      !confirmPassword
    ) {
      setMessage("Please fill all required fields.");
      setValidateBlk(true);
      return;
    }

    if (password !== confirmPassword) {
      setMessage("Passwords do not match. Try again");
      setValidateBlk(true);
      return;
    }

    if (!isValidPassword(password)) {
      setMessage("Password must be at least 8 characters long.");
      setValidateBlk(true);
      return;
    }

    if (!emailFormatValidation(email)) {
      setMessage("Email address must be valid. Please try again.");
      setValidateBlk(true);
      return;
    }
    const data = new FormData();
    data.append("firstName", firstName);
    data.append("lastName", lastName);
    data.append("companyName", companyName);
    data.append("email", email);
    data.append("password", password);
    data.append("confirmPassword", confirmPassword);

    if (fileInputRef.current?.files?.[0]) {
      data.append("profileImage", fileInputRef.current.files[0]);
    }

    try {
      setIsLoading(true);
      await createUserMutation.mutateAsync(data);
      setShowVerifyCodeModal(true);
      setSubmittedEmail(email);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <React.Fragment>
      {verifyCodeModal ? (
        <div className="input-form-flex-blk">
          <InputCodeForm code={code} setCode={setCode} email={submittedEmail} />
        </div>
      ) : (
        <div className="input-register-flex-blk">
          <div className="card">
            <form
              method="POST"
              onSubmit={(e) => {
                e.preventDefault();
                onSubmitHandler();
              }}
              noValidate>
              <h2 className="title">Create your account</h2>
              <div className="icon-register-blk" onClick={onClickUploadImage}>
                {formData.profileImage ? (
                  <div className="image-uploaded-blk">
                    <button
                      type="button"
                      className="remove-icon-btn"
                      onClick={onRemoveImageHandler}>
                      <RemoveIcon />
                    </button>
                    <img
                      src={formData.profileImage}
                      alt="Profile"
                      className="uploaded-profile-img"
                    />
                  </div>
                ) : (
                  <UploadIcon />
                )}

                <input
                  type="file"
                  ref={fileInputRef}
                  style={{display: "none"}}
                  accept="image/*"
                  onChange={onFileChangeHandler}
                />
              </div>

              <div className="register-card-flex-blk">
                <Input
                  type="text"
                  className="input"
                  placeholder=" "
                  label="First Name"
                  value={formData.firstName}
                  name="firstName"
                  onChange={onInputChangeHandler}
                  maxLength={100}
                />

                <Input
                  type="text"
                  className="input"
                  placeholder=" "
                  label="Last Name"
                  value={formData.lastName}
                  name="lastName"
                  onChange={onInputChangeHandler}
                  maxLength={100}
                />

                <Input
                  type="text"
                  className="input"
                  placeholder=" "
                  label="Company Name"
                  value={formData.companyName}
                  name="companyName"
                  onChange={onInputChangeHandler}
                  maxLength={255}
                />

                <Input
                  type="email"
                  className="input"
                  placeholder=" "
                  label="Email Address"
                  value={formData.email}
                  name="email"
                  onChange={onInputChangeHandler}
                  maxLength={100}
                />

                <Input
                  type="password"
                  className="input"
                  placeholder=" "
                  label="Password"
                  value={formData.password}
                  name="password"
                  onChange={onInputChangeHandler}
                  maxLength={100}
                />

                <Input
                  type="password"
                  className="input"
                  placeholder=" "
                  label="Confirm Password"
                  value={formData.confirmPassword}
                  name="confirmPassword"
                  onChange={onInputChangeHandler}
                  maxLength={100}
                />

                <button className="button" type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <Spinner
                      variant="small-spinner"
                      alignSpin="center-spinner"
                    />
                  ) : (
                    "Create Account"
                  )}
                </button>
              </div>
            </form>

            {validateBlk && <p className="status-message">{message}</p>}
          </div>
          <p className="powered-by-blk">
            Powered BY:<span>iCXeed</span>
          </p>
        </div>
      )}
    </React.Fragment>
  );
};

export default RegisterCard;
